import { useState } from "react"
import { useNavigate } from "react-router-dom"
import OutreachPollBanner from "@/components/OutreachPollBanner"
import OutreachEventsGrid from "@/components/OutreachEventsGrid"
import OutreachContactsGrid from "@/components/OutreachContactsGrid"
import EmailPreviewModal from "@/components/EmailPreviewModal"
import type { OutreachEvent } from "@/types/outreach"
import OutreachActionPanel from "@/components/OutreachActionPanel"
import {
  Send, CheckCircle, MessageSquare, MessageCircle,
  Upload, GitBranch, Linkedin, AlertCircle, ChevronDown, ExternalLink, ChevronLeft, ChevronRight, Search, X as XIcon,
  AlertTriangle, Lock,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useOutreachMetrics, useOutreachQueue, useConversionFunnel } from "@/hooks/useOutreach"
import { useAuthStore } from "@/stores/authStore"
import { OUTREACH_STATUS_CONFIG } from "@/types/outreach"
import type { QueueItem, OutreachStatus } from "@/types/outreach"

function MetricCard({ icon: Icon, label, value, color }: { icon: React.ElementType; label: string; value: number | string; color: string }) {
  return (
    <div className="bg-card border rounded-lg p-4 flex flex-col items-center gap-1">
      <Icon className="w-5 h-5" style={{ color }} />
      <span className="text-2xl font-bold">{value}</span>
      <span className="text-xs text-muted-foreground font-medium">{label}</span>
    </div>
  )
}

function QueueCard({ item, onClick }: { item: QueueItem; onClick: () => void }) {
  const statusCfg = OUTREACH_STATUS_CONFIG[item.outreach_status as OutreachStatus]
  const StatusIcon = statusCfg?.icon
  const displayName = item.lead_title || item.company_name
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left bg-card border rounded-lg p-4 hover:bg-accent/50 transition-colors",
        !item.is_owner && "opacity-60",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Primary line: LinkedIn button + Contact name + job title */}
          <div className="flex items-center gap-2">
            {item.contact_linkedin_url && (
              <a
                href={item.contact_linkedin_url.startsWith("http") ? item.contact_linkedin_url : `https://${item.contact_linkedin_url}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center transition-colors hover:opacity-80"
                style={{ backgroundColor: "#0A66C2" }}
                title="Abrir perfil no LinkedIn"
              >
                <Linkedin className="w-3.5 h-3.5 text-white" />
              </a>
            )}
            {!item.has_contact ? (
              <span className="flex items-center gap-1.5 font-semibold text-sm truncate text-amber-500">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                Sem contato vinculado
              </span>
            ) : item.contact_name ? (
              <span className="font-semibold text-sm truncate">
                {item.contact_name}
                {item.contact_job_title && (
                  <span className="font-normal text-muted-foreground"> — {item.contact_job_title}</span>
                )}
              </span>
            ) : (
              <span className="font-semibold text-sm truncate text-muted-foreground italic">Sem contato</span>
            )}
          </div>

          {/* Secondary line: Lead title or company name */}
          <p className="text-xs text-muted-foreground mt-0.5 truncate pl-0">
            {displayName}
          </p>

          {/* Third line: Status + next action */}
          <div className="flex items-center gap-1.5 mt-1">
            {StatusIcon && <StatusIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: statusCfg.color }} />}
            <span className="text-xs" style={{ color: statusCfg?.color }}>{statusCfg?.title}</span>
            <span className="text-xs text-muted-foreground">|</span>
            <span className="text-xs text-muted-foreground">{item.next_action}</span>
          </div>

          {/* Ownership + cadence badges */}
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            {!item.is_owner && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                <Lock className="w-2.5 h-2.5" />
                Somente visualizacao
              </span>
            )}
            {item.cadence_name && (
              <span className="inline-block text-[10px] font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {item.cadence_name} {item.cadence_step !== null ? `(${item.cadence_step})` : ""}
              </span>
            )}
          </div>
        </div>
        {item.days_overdue > 0 && (
          <span className="flex items-center gap-1 text-xs font-semibold text-destructive bg-destructive/10 px-2 py-1 rounded-full flex-shrink-0">
            <AlertCircle className="w-3 h-3" />
            {item.days_overdue}d
          </span>
        )}
      </div>
    </button>
  )
}

export default function OutreachPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const metrics = useOutreachMetrics()
  const funnel = useConversionFunnel()

  // Active tab
  const [activeTab, setActiveTab] = useState<"queue" | "contacts">("queue")

  // Email preview modal
  const [emailPreviewEvent, setEmailPreviewEvent] = useState<OutreachEvent | null>(null)

  // Queue filter + pagination state (declarados antes do hook que os consome)
  const [queueSearch, setQueueSearch] = useState('')
  const [queueDebouncedSearch, setQueueDebouncedSearch] = useState('')
  const [queueStatusFilter, setQueueStatusFilter] = useState('')
  const [queuePage, setQueuePage] = useState(1)
  const queuePerPage = 15

  const queue = useOutreachQueue({
    search: queueDebouncedSearch || undefined,
    outreach_status: queueStatusFilter || undefined,
    page: queuePage,
    per_page: queuePerPage,
  })

  const [queueTimer, setQueueTimer] = useState<NodeJS.Timeout | null>(null)
  const handleQueueSearchChange = (val: string) => {
    setQueueSearch(val)
    if (queueTimer) clearTimeout(queueTimer)
    const t = setTimeout(() => {
      setQueueDebouncedSearch(val)
      setQueuePage(1)
    }, 400)
    setQueueTimer(t)
  }

  // Action panel state
  const [panelOpen, setPanelOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<QueueItem | null>(null)

  const [panelReadOnly, setPanelReadOnly] = useState(false)

  const handleCardClick = (item: QueueItem) => {
    setSelectedItem(item)
    setPanelReadOnly(!item.is_owner)
    setPanelOpen(true)
  }

  const handlePanelClose = (open: boolean) => {
    setPanelOpen(open)
    if (!open) {
      // Small delay before clearing item to allow close animation
      setTimeout(() => setSelectedItem(null), 300)
    }
  }

  const isLoading = metrics.isLoading && queue.isLoading

  const funnelSteps = funnel.data
    ? [
        { label: "Convite", value: funnel.data.invite_sent, color: "#3b82f6" },
        { label: "Conectado", value: funnel.data.connected, color: "#22c55e" },
        { label: "Msg Env.", value: funnel.data.msg_sent, color: "#8b5cf6" },
        { label: "Respondeu", value: funnel.data.replied, color: "#f59e0b" },
        { label: "Qualif.", value: funnel.data.qualified, color: "#10b981" },
        { label: "Oport.", value: funnel.data.opportunity, color: "#f97316" },
      ]
    : []

  const maxFunnelValue = Math.max(...funnelSteps.map((s) => s.value), 1)

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Outreach</h1>
        <p className="text-muted-foreground mt-1">
          LinkedIn Prospeccao{user?.first_name ? ` — ${user.first_name}` : ""}
        </p>
      </div>

      {/* Email Monitoring Status */}
      <OutreachPollBanner />

      {/* Quick Actions */}
      <div className="grid grid-cols-3 gap-3">
        <button
          onClick={() => navigate("/outreach/csv-import")}
          className="flex flex-col items-center gap-2 p-4 bg-card border rounded-xl hover:bg-accent/50 transition-colors"
        >
          <Upload className="w-5 h-5 text-blue-500" />
          <span className="text-xs font-semibold">Importar CSV</span>
        </button>
        <button
          onClick={() => navigate("/outreach/cadences")}
          className="flex flex-col items-center gap-2 p-4 bg-card border rounded-xl hover:bg-accent/50 transition-colors"
        >
          <GitBranch className="w-5 h-5 text-violet-500" />
          <span className="text-xs font-semibold">Cadencias</span>
        </button>
        <a
          href="https://www.linkedin.com/sales/home"
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center gap-2 p-4 bg-card border rounded-xl hover:bg-accent/50 transition-colors"
        >
          <Linkedin className="w-5 h-5 text-[#0a66c2]" />
          <span className="text-xs font-semibold">Sales Nav</span>
        </a>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-1 bg-muted/50 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab("queue")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "queue" ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}
        >
          Fila de Ações
        </button>
        <button
          onClick={() => setActiveTab("contacts")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "contacts" ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}
        >
          Todos os Contatos
        </button>
      </div>

      {/* KPI Cards */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : metrics.data ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard icon={Send} label="Convites" value={metrics.data.invites_sent} color="#3b82f6" />
            <MetricCard icon={CheckCircle} label="Conectados" value={metrics.data.invites_accepted} color="#22c55e" />
            <MetricCard icon={MessageSquare} label="Msgs Env." value={metrics.data.messages_sent} color="#8b5cf6" />
            <MetricCard icon={MessageCircle} label="Respostas" value={metrics.data.messages_received} color="#f59e0b" />
          </div>
          <div className="bg-card border rounded-lg flex items-center divide-x">
            <div className="flex-1 text-center py-3">
              <p className="text-lg font-bold text-green-500">{(metrics.data.acceptance_rate * 100).toFixed(0)}%</p>
              <p className="text-xs text-muted-foreground">Aceitacao</p>
            </div>
            <div className="flex-1 text-center py-3">
              <p className="text-lg font-bold text-amber-500">{(metrics.data.reply_rate * 100).toFixed(0)}%</p>
              <p className="text-xs text-muted-foreground">Resposta</p>
            </div>
            <div className="flex-1 text-center py-3">
              <p className="text-lg font-bold text-primary">{metrics.data.leads_created}</p>
              <p className="text-xs text-muted-foreground">Leads</p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Conversion Funnel */}
      {funnelSteps.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3">Funil de Conversao</h2>
          <div className="bg-card border rounded-lg p-4 space-y-2">
            {funnelSteps.map((step, idx) => (
              <div key={step.label} className="flex items-center gap-3">
                <span className="w-16 text-xs text-muted-foreground font-medium text-right">{step.label}</span>
                <div className="flex-1 h-4 bg-muted rounded overflow-hidden">
                  <div
                    className="h-full rounded transition-all duration-500"
                    style={{
                      width: `${(step.value / maxFunnelValue) * 100}%`,
                      backgroundColor: step.color,
                      minWidth: step.value > 0 ? "4px" : "0",
                    }}
                  />
                </div>
                <span className="w-8 text-sm font-bold text-right">{step.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "queue" && (<>
      {/* Action Queue */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Fila de A\u00e7\u00f5es</h2>
          <div className="flex items-center gap-2">
            {(queue.data?.overdue ?? 0) > 0 && (
              <span className="flex items-center gap-1 text-xs font-semibold text-destructive bg-destructive/10 px-2.5 py-1 rounded-full">
                <AlertCircle className="w-3.5 h-3.5" />
                {queue.data!.overdue} atrasado{queue.data!.overdue > 1 ? "s" : ""}
              </span>
            )}
            <span className="text-xs text-muted-foreground">{queue.data?.total ?? 0} leads</span>
          </div>
        </div>

        {/* Queue Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={queueSearch}
              onChange={(e) => handleQueueSearchChange(e.target.value)}
              placeholder="Buscar por nome, empresa, lead..."
              className="w-full pl-9 pr-8 py-2 text-sm bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {queueSearch && (
              <button onClick={() => { setQueueSearch(''); setQueueDebouncedSearch(''); setQueuePage(1) }} className="absolute right-2 top-1/2 -translate-y-1/2">
                <XIcon className="w-4 h-4 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>
          <select
            value={queueStatusFilter}
            onChange={(e) => { setQueueStatusFilter(e.target.value); setQueuePage(1) }}
            className="px-3 py-2 text-sm bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">Todos os status</option>
            <option value="not_contacted">N\u00e3o contatado</option>
            <option value="invite_sent">Convite enviado</option>
            <option value="connected">Conectado</option>
            <option value="msg_sent">Mensagem enviada</option>
            <option value="replied">Respondeu</option>
            <option value="follow_up">Follow-up</option>
            <option value="qualified">Qualificado</option>
          </select>
        </div>

        {queue.isLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : queue.data?.items?.length ? (
          <>
            <div className="space-y-2">
              {queue.data.items.map((item) => (
                <QueueCard
                  key={item.lead_id}
                  item={item}
                  onClick={() => handleCardClick(item)}
                />
              ))}
            </div>
            {/* Pagination */}
            {(queue.data.total_pages ?? 1) > 1 && (
              <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                <span>P\u00e1gina {queuePage} de {queue.data.total_pages} ({queue.data.total} leads)</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setQueuePage(p => Math.max(1, p - 1))}
                    disabled={queuePage <= 1}
                    className="p-1.5 rounded border hover:bg-accent disabled:opacity-30 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setQueuePage(p => Math.min(queue.data!.total_pages ?? 1, p + 1))}
                    disabled={queuePage >= (queue.data.total_pages ?? 1)}
                    className="p-1.5 rounded border hover:bg-accent disabled:opacity-30 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-card border rounded-lg p-8 text-center">
            <CheckCircle className="w-7 h-7 text-green-500 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {queueDebouncedSearch || queueStatusFilter ? "Nenhum lead encontrado com esse filtro" : "Nenhuma a\u00e7\u00e3o pendente"}
            </p>
          </div>
        )}
      </div>

      {/* LinkedIn Events Grid */}
      <OutreachEventsGrid onRowClick={(event) => setEmailPreviewEvent(event)} />
      </>)}

      {/* Contacts Grid Tab */}
      {activeTab === "contacts" && (
        <div>
          <h2 className="text-lg font-semibold mb-3">Contatos dos Leads</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Todos os contatos de todos os leads atribuídos a você. Busque por nome, email, empresa, cargo ou status.
          </p>
          <OutreachContactsGrid />
        </div>
      )}

      {/* Email Preview Modal */}
      <EmailPreviewModal
        event={emailPreviewEvent}
        onClose={() => setEmailPreviewEvent(null)}
      />

      {/* Action Panel (SlideOver) */}
      <OutreachActionPanel
        item={selectedItem}
        open={panelOpen}
        onOpenChange={handlePanelClose}
        readOnly={panelReadOnly}
      />
    </div>
  )
}
